/**
 * Sticky Section Nav — scroll spy + smooth scroll
 * Independent from sticky-navigation.liquid inline script
 */
(function () {
  'use strict';

  class SectionNav {
    constructor(element) {
      this.element = element;
      this.navLinks = element.querySelectorAll('.section-nav-inner a');
      this.scrollTimeout = null;
      this.scrollLockUntil = 0;
      this.init();
    }

    /** Sticky is applied on the Shopify section wrapper, not .section-nav itself. */
    getStickyRoot() {
      return this.element.closest('[id^="shopify-section-"]') || this.element;
    }

    /**
     * Offset used when scrolling to a section — must match sticky top + bar height.
     * Sticky CSS: top: calc(var(--sticky-offset) + var(--header-height))
     */
    getScrollOffset() {
      const root = this.getStickyRoot();
      const stickyTop = parseFloat(getComputedStyle(root).top) || 0;
      const height = root.offsetHeight || this.element.offsetHeight || 0;
      return stickyTop + height + 8;
    }

    /** Viewport Y where a section is considered "active" (just under the sticky bar). */
    getActivationLine() {
      return this.getStickyRoot().getBoundingClientRect().bottom + 8;
    }

    init() {
      this.navLinks.forEach((link) => {
        if (!link.dataset.clickListenerAttached) {
          link.addEventListener('click', (e) => {
            this.handleClick(e, link);
          });
          link.dataset.clickListenerAttached = 'true';
        }
      });

      if (this.navLinks.length > 0) {
        setTimeout(() => {
          this.updateActiveLink();
        }, 100);

        window.addEventListener(
          'scroll',
          () => {
            if (this.scrollTimeout) {
              cancelAnimationFrame(this.scrollTimeout);
            }
            this.scrollTimeout = requestAnimationFrame(() => {
              this.updateActiveLink();
            });
          },
          { passive: true }
        );
      }
    }

    resolveSelector(targetValue) {
      const value = (targetValue || '').trim();
      if (!value) return '';

      if (value.startsWith('shopify-section-')) {
        return `#${value}`;
      }
      if (value.startsWith('#')) {
        return value;
      }
      if (value.startsWith('template--')) {
        return `#${value}`;
      }
      if (!/^[a-zA-Z0-9_.-]+$/.test(value)) {
        return '';
      }
      return `#shopify-section-${value}`;
    }

    findTarget(targetValue) {
      const selector = this.resolveSelector(targetValue);
      if (!selector) return null;

      let targetElement = document.querySelector(selector);
      if (!targetElement && selector.startsWith('#')) {
        targetElement = document.querySelector(selector.substring(1));
      }
      if (!targetElement) {
        targetElement = document.querySelector(`[id*="${targetValue}"]`);
      }
      return targetElement;
    }

    handleClick(e, link) {
      e.preventDefault();
      e.stopPropagation();

      const targetValue = (link.dataset.targetValue || link.getAttribute('href') || '').trim();
      if (!targetValue || targetValue === '#') {
        console.warn('Section Nav: No target specified for link');
        return;
      }

      const targetElement = this.findTarget(targetValue.replace(/^#/, ''));
      if (!targetElement) {
        console.warn('Section Nav: Target element not found:', targetValue);
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          alert(`无法找到目标元素: ${targetValue}\n请检查 Section ID 是否正确。`);
        }
        return;
      }

      try {
        const offset = this.getScrollOffset();

        const currentScrollY =
          window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
        const elementTop = targetElement.getBoundingClientRect().top + currentScrollY;
        const offsetPosition = Math.max(0, elementTop - offset);

        this.setActiveLink(link);
        // Keep click target active while smooth scroll settles (spy would otherwise lag).
        this.scrollLockUntil = Date.now() + 900;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      } catch (error) {
        console.error('Section Nav: Error during scroll:', error);
        try {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          this.setActiveLink(link);
          this.scrollLockUntil = Date.now() + 900;
        } catch (fallbackError) {
          console.error('Section Nav: Fallback scroll also failed:', fallbackError);
        }
      }
    }

    setActiveLink(activeLink) {
      this.navLinks.forEach((link) => {
        link.classList.remove('active');
      });
      if (activeLink) {
        activeLink.classList.add('active');
      }
    }

    updateActiveLink() {
      if (Date.now() < this.scrollLockUntil) return;

      // Must include sticky header offset — previously only used navHeight, so on
      // mobile click-scroll landed short of the activation line and needed extra scroll.
      const activationLine = this.getActivationLine();
      const currentScrollY = window.pageYOffset || window.scrollY;

      const sections = [];
      this.navLinks.forEach((link) => {
        const targetValue = (link.dataset.targetValue || link.getAttribute('href') || '')
          .trim()
          .replace(/^#/, '');
        if (!targetValue) return;

        const targetElement = this.findTarget(targetValue);
        if (targetElement) {
          const rect = targetElement.getBoundingClientRect();
          sections.push({
            link,
            top: rect.top + currentScrollY,
            rect,
          });
        }
      });

      sections.sort((a, b) => a.top - b.top);

      let current = sections.length > 0 ? sections[0].link : null;
      sections.forEach((section) => {
        if (section.rect.top <= activationLine) {
          current = section.link;
        }
      });

      if (current) {
        this.setActiveLink(current);
      }
    }
  }

  function ensureHeaderHeight() {
    const headerHeight = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height')
      .trim();
    if (!headerHeight || headerHeight === '0px' || headerHeight === '') {
      const header =
        document.querySelector('header[is="sticky-header"]') || document.querySelector('header');
      if (header) {
        const height = header.offsetHeight;
        if (height > 0) {
          document.documentElement.style.setProperty('--header-height', `${height}px`);
        }
      }
    }
  }

  function unlockOverflowParents(element) {
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
      const computedStyle = window.getComputedStyle(parent);
      const overflow = computedStyle.overflow;
      const overflowY = computedStyle.overflowY;
      const overflowX = computedStyle.overflowX;

      if (
        overflow === 'hidden' ||
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'hidden' ||
        overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowX === 'hidden' ||
        overflowX === 'auto' ||
        overflowX === 'scroll'
      ) {
        if (parent.id && parent.id.startsWith('shopify-section-')) {
          parent.style.overflow = 'visible';
          parent.style.overflowY = 'visible';
          parent.style.overflowX = 'visible';
        }
      }

      if (parent.tagName === 'BODY' || parent.tagName === 'MAIN' || parent.id === 'MainContent') {
        break;
      }

      parent = parent.parentElement;
    }
  }

  function initSectionNav() {
    document.querySelectorAll('.section-nav').forEach((element) => {
      if (element.dataset.initialized === 'true') return;

      unlockOverflowParents(element);
      ensureHeaderHeight();
      setTimeout(ensureHeaderHeight, 100);
      setTimeout(ensureHeaderHeight, 500);

      new SectionNav(element);
      element.dataset.initialized = 'true';
    });
  }

  function initWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(initSectionNav, 100);
      });
    } else {
      setTimeout(initSectionNav, 100);
    }
  }

  initWhenReady();
  window.addEventListener('load', () => {
    setTimeout(initSectionNav, 100);
  });

  /* ---------- Design mode helpers ---------- */
  function initHelperButton() {
    document.querySelectorAll('.section-nav-helper-btn').forEach((btn) => {
      if (btn.dataset.initialized === 'true') return;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof window.showAllSectionIds === 'function') {
          window.showAllSectionIds();
        } else {
          console.warn('Section Nav: showAllSectionIds function not found');
        }
      });
      btn.dataset.initialized = 'true';
    });
  }

  function showHelperButtons() {
    const containers = document.querySelectorAll('.section-nav-helper');
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      containers.forEach((container) => {
        container.style.display = 'block';
        container.style.pointerEvents = 'auto';
      });
      initHelperButton();
      return true;
    }
    containers.forEach((container) => {
      container.style.display = 'none';
      container.style.pointerEvents = 'none';
    });
    return false;
  }

  function initHelperButtons() {
    showHelperButtons();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', showHelperButtons);
    } else {
      showHelperButtons();
    }
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      const checkInterval = setInterval(showHelperButtons, 1000);
      setTimeout(() => clearInterval(checkInterval), 10000);
    }
  }

  initHelperButtons();

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      const sectionId = event.detail.sectionId;
      const element = document.querySelector(`#shopify-section-${sectionId} .section-nav`);
      if (element) {
        element.dataset.initialized = 'false';
        initSectionNav();
      }
      setTimeout(showHelperButtons, 100);
    });

    document.addEventListener('shopify:section:select', function () {
      setTimeout(showHelperButtons, 100);
    });
  }

  if (typeof window.showAllSectionIds === 'undefined') {
    window.showAllSectionIds = function () {
      const sections = [];
      document.querySelectorAll('[id^="shopify-section-"]').forEach((section) => {
        const sectionId = section.id.replace('shopify-section-', '');

        if (
          sectionId.includes('header') ||
          sectionId.includes('footer') ||
          sectionId.includes('cart-drawer') ||
          sectionId.includes('search-drawer') ||
          sectionId.includes('quick-view') ||
          sectionId.includes('sticky-navigation') ||
          sectionId.includes('sticky-section-nav') ||
          sectionId.includes('sticky-atc-bar') ||
          sectionId.includes('overlay')
        ) {
          return;
        }

        let sectionName = sectionId;
        const heading = section.querySelector(
          'h1, h2, h3, h4, h5, h6, .section__heading, .section-heading'
        );
        if (heading && heading.textContent.trim()) {
          sectionName = heading.textContent.trim().substring(0, 50);
        }

        sections.push({
          id: sectionId,
          name: sectionName,
        });
      });

      const modal = document.createElement('div');
      modal.style.cssText =
        'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,.7);z-index:999999;display:flex;align-items:center;justify-content:center;font-family:sans-serif;';

      const content = document.createElement('div');
      content.style.cssText =
        'background:#fff;border-radius:8px;padding:30px;max-width:800px;max-height:80vh;overflow-y:auto;box-shadow:0 4px 20px rgba(0,0,0,.3);';

      const title = document.createElement('h2');
      title.textContent = '页面上的所有 Section ID';
      title.style.cssText = 'margin:0 0 20px 0;color:#333;font-size:24px;';

      const instructions = document.createElement('p');
      instructions.innerHTML =
        '<strong>使用方法：</strong>复制 Section ID，粘贴到 Sticky Section Nav 配置中的 "Section ID" 字段';
      instructions.style.cssText = 'margin:0 0 20px 0;color:#666;line-height:1.6;';

      const list = document.createElement('div');
      list.style.cssText = 'display:flex;flex-direction:column;gap:10px;';

      sections.forEach((section) => {
        const item = document.createElement('div');
        item.style.cssText =
          'padding:15px;border:1px solid #e0e0e0;border-radius:6px;background:#f9f9f9;';

        const idLabel = document.createElement('div');
        idLabel.style.cssText = 'font-weight:bold;color:#2196F3;margin-bottom:5px;font-size:14px;';
        idLabel.textContent = `Section ID: ${section.id}`;

        const nameLabel = document.createElement('div');
        nameLabel.style.cssText = 'color:#666;font-size:12px;margin-bottom:10px;';
        nameLabel.textContent = `名称: ${section.name}`;

        const copyBtn = document.createElement('button');
        copyBtn.textContent = '复制 ID';
        copyBtn.type = 'button';
        copyBtn.style.cssText =
          'padding:6px 12px;background:#2196F3;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;';
        copyBtn.onclick = (e) => {
          e.stopPropagation();
          const textArea = document.createElement('textarea');
          textArea.value = section.id;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            copyBtn.textContent = '已复制';
            copyBtn.style.background = '#4CAF50';
            setTimeout(() => {
              copyBtn.textContent = '复制 ID';
              copyBtn.style.background = '#2196F3';
            }, 2000);
          } catch (err) {
            copyBtn.textContent = '复制失败';
            setTimeout(() => {
              copyBtn.textContent = '复制 ID';
            }, 2000);
          }
          document.body.removeChild(textArea);
        };

        item.appendChild(idLabel);
        item.appendChild(nameLabel);
        item.appendChild(copyBtn);
        list.appendChild(item);
      });

      const closeBtn = document.createElement('button');
      closeBtn.textContent = '关闭';
      closeBtn.type = 'button';
      closeBtn.style.cssText =
        'margin-top:20px;padding:10px 20px;background:#f44336;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:14px;width:100%;';
      closeBtn.onclick = () => {
        if (document.body.contains(modal)) {
          document.body.removeChild(modal);
        }
      };

      content.appendChild(title);
      content.appendChild(instructions);
      content.appendChild(list);
      content.appendChild(closeBtn);
      modal.appendChild(content);
      document.body.appendChild(modal);

      return sections;
    };
  }
})();
